import { BlueprintInstanceType } from '../../shared.enums';

import type { Connection } from '../../connections/connection.class';
import type { Island } from '../../islands/island.class';
import type { Panel } from '../../panels/panel.class';

export type SeamConnectionMatch = {
	connectedPanel: Panel;
	connection: Connection;
	// read from the connection's SOURCE half - Connection.padding reports 0 on the target half
	padding: number;
	// islandsTouch evaluated on that same source half, so no caller has to re-derive which half to test
	touching: boolean;
};

// geometric contact test - deliberately NOT the passthrough rule, which is free to tighten independently.
// Sole owner of this predicate: both height derivation (get-flat-trim-dimensions.cfn.ts) and pairing
// (get-paired-seam-trim.cfn.ts) resolve their seam through the finders below rather than calling this directly
export const islandsTouch = (sourceHalf: Connection): boolean => {
	return sourceHalf.sourcePanel !== null && sourceHalf.targetPanel !== null && sourceHalf.padding <= 1;
};

// every island-to-island connection landing on either of the trim's panels, in panel order, contact test
// recorded rather than applied - the two exported finders below differ only in how they use it
const collectSeamConnections = (pair: Array<Panel>, island: Island): Array<SeamConnectionMatch> => {
	const matches: Array<SeamConnectionMatch> = [];

	pair.forEach((connectedPanel) => {
		island.connections.getConnectionsBySourcePanelId(connectedPanel.id).forEach((connection) => {
			if (connection.targetIsland?.instanceOf !== BlueprintInstanceType.Island) return;

			// padding only reads true on the source half - Connection.padding returns 0 on the target half
			const sourceHalf = connection.isSource ? connection : connection.targetConnection;
			if (!sourceHalf) return;

			matches.push({ connectedPanel, connection, padding: sourceHalf.padding, touching: islandsTouch(sourceHalf) });
		});
	});

	return matches;
};

/**
 * Walks a trim's own two panels for an island-to-island connection whose islands actually touch.
 * Unlike a side-panel-gated resolution, this accepts ANY panel role on either end - an L-shape routinely
 * joins one island's front/back panel to the other's side panel, so requiring a side panel on the connected
 * panel would silently miss (or disagree on) half of every L-shaped seam. Used by both
 * `get-paired-seam-trim.cfn.ts` (pairing) and `get-flat-trim-dimensions.cfn.ts` (height derivation), so the
 * two always resolve the identical seam and therefore always agree with each other.
 * @param pair the trim's two panels
 * @param island the panels' own island
 * @returns the connected panel, its connection, and the seam's padding (source-half), or null when this corner has no adjoining touching island
 */
export const findSeamConnection = (pair: Array<Panel>, island: Island): SeamConnectionMatch | null => {
	return collectSeamConnections(pair, island).find(({ touching }) => touching) ?? null;
};

/**
 * The same seam lookup with the `islandsTouch` contact test relaxed - a seam still resolves however wide the
 * padding gap is. Flat trim GEOMETRY uses this: its two 1.5" halves are shifted toward the seam by
 * `padding / 2` so they meet at the centre of the gap, and gating that on contact would make a too-wide gap
 * snap the halves back onto the island faces, hiding the very problem the centre alignment exists to expose.
 * A touching seam still wins when one exists, so nothing about contacting-island layouts changes.
 * @param pair the trim's two panels
 * @param island the panels' own island
 * @returns the connected panel, its connection, and the seam's padding (source-half), or null for a genuinely free corner
 */
export const findSeamConnectionAtAnyGap = (pair: Array<Panel>, island: Island): SeamConnectionMatch | null => {
	const matches = collectSeamConnections(pair, island);

	return matches.find(({ touching }) => touching) ?? matches[0] ?? null;
};
